import React from 'react';
import { motion } from 'framer-motion';
import {
  FiInbox,
  FiSearch,
  FiAlertCircle,
  FiFileText,
  FiDatabase,
} from 'react-icons/fi';
import { cn } from '../../lib/utils';
import { Button } from './Button';

/**
 * EmptyState Component
 *
 * A flexible empty state component for various no-data scenarios
 *
 * @param {Object} props - Component props
 * @param {'no-data'|'search'|'error'|'no-results'|'custom'} props.variant - Empty state variant
 * @param {string} props.title - Main heading
 * @param {string} props.description - Description text
 * @param {React.ReactNode} props.icon - Custom icon
 * @param {string} props.actionLabel - CTA button label
 * @param {Function} props.onAction - CTA button click handler
 * @param {React.ReactNode} props.children - Custom content
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.illustration - Custom illustration
 *
 * @example
 * <EmptyState
 *   variant="no-data"
 *   title="No Applications Yet"
 *   description="You haven't submitted any applications. Get started now!"
 *   actionLabel="Start Application"
 *   onAction={() => navigate('/apply')}
 * />
 */
const EmptyState = ({
  variant = 'no-data',
  title,
  description,
  icon,
  actionLabel,
  onAction,
  children,
  className,
  illustration,
}) => {
  // Variant configurations
  const variantConfig = {
    'no-data': {
      icon: <FiInbox className="h-12 w-12" />,
      iconBg: 'bg-neutral-100',
      iconColor: 'text-neutral-400',
      defaultTitle: 'No Data Available',
      defaultDescription: 'There are no items to display at the moment.',
    },
    'search': {
      icon: <FiSearch className="h-12 w-12" />,
      iconBg: 'bg-primary-100',
      iconColor: 'text-primary-600',
      defaultTitle: 'No Results Found',
      defaultDescription: 'We couldn\'t find anything matching your search. Try different keywords.',
    },
    'error': {
      icon: <FiAlertCircle className="h-12 w-12" />,
      iconBg: 'bg-danger-100',
      iconColor: 'text-danger-600',
      defaultTitle: 'Something Went Wrong',
      defaultDescription: 'We encountered an error loading this data. Please try again.',
    },
    'no-results': {
      icon: <FiFileText className="h-12 w-12" />,
      iconBg: 'bg-warning-100',
      iconColor: 'text-warning-600',
      defaultTitle: 'No Results',
      defaultDescription: 'No records match your current filters.',
    },
    'custom': {
      icon: <FiDatabase className="h-12 w-12" />,
      iconBg: 'bg-neutral-100',
      iconColor: 'text-neutral-400',
      defaultTitle: 'Empty',
      defaultDescription: 'No content available.',
    },
  };

  const config = variantConfig[variant] || variantConfig['no-data'];
  const displayTitle = title || config.defaultTitle;
  const displayDescription = description || config.defaultDescription;
  const displayIcon = icon || config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-4',
        className
      )}
    >
      {/* Illustration or Icon */}
      {illustration ? (
        <div className="mb-6">{illustration}</div>
      ) : (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.1,
          }}
          className={cn(
            'h-24 w-24 rounded-full flex items-center justify-center mb-6',
            config.iconBg
          )}
        >
          <div className={config.iconColor}>{displayIcon}</div>
        </motion.div>
      )}

      {/* Title */}
      <motion.h3
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-xl font-bold text-neutral-900 mb-2"
      >
        {displayTitle}
      </motion.h3>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-sm text-neutral-600 max-w-md mb-6"
      >
        {displayDescription}
      </motion.p>

      {/* Custom Content */}
      {children && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="w-full max-w-md"
        >
          {children}
        </motion.div>
      )}

      {/* Action Button */}
      {actionLabel && onAction && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Button onClick={onAction} variant="primary">
            {actionLabel}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

/**
 * NoApplications Component
 * Specialized empty state for no applications
 */
export const NoApplications = ({ onStartApplication }) => (
  <EmptyState
    variant="no-data"
    title="No Applications Yet"
    description="You haven't started any applications. Begin your admission journey now!"
    actionLabel="Start New Application"
    onAction={onStartApplication}
  />
);

/**
 * NoPayments Component
 * Specialized empty state for no payments
 */
export const NoPayments = () => (
  <EmptyState
    variant="no-data"
    title="No Payment History"
    description="You haven't made any payments yet. Your payment history will appear here once you complete a transaction."
  />
);

/**
 * SearchNoResults Component
 * Specialized empty state for search results
 */
export const SearchNoResults = ({ searchTerm, onClearSearch }) => (
  <EmptyState
    variant="search"
    title={`No results for "${searchTerm}"`}
    description="Try adjusting your search terms or filters to find what you're looking for."
    actionLabel="Clear Search"
    onAction={onClearSearch}
  />
);

/**
 * ErrorState Component
 * Specialized empty state for errors
 */
export const ErrorState = ({ onRetry, message }) => (
  <EmptyState
    variant="error"
    title="Oops! Something Went Wrong"
    description={message || "We encountered an unexpected error. Please try again."}
    actionLabel="Try Again"
    onAction={onRetry}
  />
);

/**
 * NoDocuments Component
 * Specialized empty state for no documents
 */
export const NoDocuments = ({ onUpload }) => (
  <EmptyState
    variant="no-data"
    icon={<FiFileText className="h-12 w-12" />}
    title="No Documents Uploaded"
    description="Upload your required documents to complete your application."
    actionLabel="Upload Documents"
    onAction={onUpload}
  />
);

/**
 * ComingSoon Component
 * Specialized empty state for coming soon features
 */
export const ComingSoon = ({ featureName }) => (
  <EmptyState
    variant="custom"
    title="Coming Soon"
    description={`${featureName || 'This feature'} is currently under development. Check back soon!`}
  />
);

export default EmptyState;
